import { useState, useCallback, useEffect, type FormEvent } from "react";

export type TemplateDocumentNameModalProps = {
  /** When set, the dialog is open for this request (caller may use a counter or id). */
  readonly open: boolean;
  readonly isBusy?: boolean;
  readonly onCancel: () => void;
  /** Called with trimmed non-empty name. */
  readonly onConfirm: (displayName: string) => void;
};

/**
 * Captures a document/contract name before `POST /generated-contracts-v2/draft` and opening Word.
 * Cancel or empty/whitespace submit does not call `onConfirm`.
 */
export const TemplateDocumentNameModal = ({
  open,
  isBusy = false,
  onCancel,
  onConfirm
}: TemplateDocumentNameModalProps) => {
  const [value, setValue] = useState("");
  const [showEmptyError, setShowEmptyError] = useState(false);

  useEffect(() => {
    if (open) {
      setValue("");
      setShowEmptyError(false);
    }
  }, [open]);

  const handleClose = useCallback(() => {
    if (isBusy) return;
    onCancel();
  }, [isBusy, onCancel]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const t = value.trim();
      if (!t) {
        setShowEmptyError(true);
        return;
      }
      onConfirm(t);
    },
    [onConfirm, value]
  );

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="template-doc-name-title"
    >
      <div className="w-full max-w-md rounded-lg border border-[var(--border)] bg-white p-5 shadow-lg">
        <h2 id="template-doc-name-title" className="text-base font-semibold text-foreground">
          Document Name
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Enter a name for this contract before the draft is created and opened in Word.
        </p>
        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          <div>
            <label
              htmlFor="template-document-name"
              className="mb-1 block text-sm font-medium text-foreground"
            >
              Contract Name <span className="text-destructive">*</span>
            </label>
            <input
              id="template-document-name"
              type="text"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setShowEmptyError(false);
              }}
              disabled={isBusy}
              autoFocus
              className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm disabled:opacity-60"
              aria-invalid={showEmptyError}
              aria-describedby={showEmptyError ? "template-document-name-error" : undefined}
            />
            {showEmptyError ? (
              <p id="template-document-name-error" className="mt-1 text-xs text-destructive" role="alert">
                Enter a document name, or click Cancel to go back.
              </p>
            ) : null}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={handleClose}
              disabled={isBusy}
              className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm font-medium transition-all duration-200 ease-in-out hover:bg-accent disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isBusy}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-all duration-200 ease-in-out disabled:opacity-60"
            >
              {isBusy ? "Working…" : "Continue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
